<template>
    <div class="print-error-toast" :class="toastConfig.className">
        <div class="print-error-toast__content">
            <b-avatar
                size="16px"
                class="print-error-toast__icon mr-75 flex-shrink-0"
            >
                <img
                    width="16"
                    height="16"
                    :src="toastConfig.icon"
                    :alt="variant"
                />
            </b-avatar>
            <div class="print-error-toast__text-wrapper">
                <p v-if="title" class="print-error-toast__title">{{ title }}</p>
                <div class="print-error-toast__text">
                    <p
                        v-for="(line, index) in textLines"
                        :key="index"
                        :class="{ 'mt-1': index > 0 }"
                    >
                        {{ line }}
                    </p>
                </div>
            </div>
            <span
                class="cursor-pointer toastification__close-icon ml-auto"
                data-testid="dp-print-toast-close"
                @click="$emit('close-toast')"
            >
                <feather-icon size="18" icon="XIcon" class="mb-2" />
            </span>
        </div>
    </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { BAvatar } from 'bootstrap-vue'

import errorIcon from '@/assets/images/icons/toast/error-triangle-fill.svg'
import infoIcon from '@/assets/images/icons/toast/info-circle-fill.svg'
import warningIcon from '@/assets/images/icons/toast/warning-circle-fill.svg'

export default {
    name: 'PrintErrorToast',
    components: {
        BAvatar,
    },
    props: {
        variant: {
            type: String,
            default: 'error',
        },
        icon: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: null,
        },
        text: {
            type: String,
            default: null,
        },
        hideClose: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const textLines = computed(() => {
            if (!props.text) return []
            return props.text.split('\n')
        })

        const toastConfig = computed(() => {
            const icons = {
                error: errorIcon,
                warning: warningIcon,
                info: infoIcon,
            }
            const configs = {
                error: {
                    className: 'print-error-toast--error',
                    textColor: '#842029',
                },
                warning: {
                    className: 'print-error-toast--warning',
                    textColor: '#664d03',
                },
                info: {
                    className: 'print-error-toast--info',
                    textColor: '#343a40',
                },
            }
            const variant = props.variant || 'error'
            const config = configs[variant] || configs.error
            return {
                ...config,
                icon: props.icon || icons[variant] || icons.error,
            }
        })

        return {
            textLines,
            toastConfig,
        }
    },
}
</script>

<style scoped lang="scss">
.print-error-toast {
    &__content {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        max-width: 480px;
        padding: 8px 16px;
        border-radius: 6px;
    }

    &__icon {
        margin-top: 10px;
    }

    &__text-wrapper {
        padding-top: 8px;
        flex: 1;
    }

    &__title,
    &__text {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-style: normal;
        line-height: 16px;
        white-space: pre-line;
        margin: 0;
    }

    &__title {
        font-weight: 600;
        margin-bottom: 4px;
    }

    &--error {
        .print-error-toast__content {
            background-color: #f8d7da;
            color: #842029;
            border: 1px solid #f5c2c7;
        }

        .print-error-toast__icon {
            background-color: #f8d7da;
        }
    }

    &--warning {
        .print-error-toast__content {
            background-color: #fff3cd;
            color: #664d03;
            border: 1px solid #ffda6a;
        }

        .print-error-toast__icon {
            background-color: #fff3cd;
        }
    }

    &--info {
        .print-error-toast__content {
            background-color: #e9ecef;
            color: #343a40;
            border: 1px solid #ced4da;
        }

        .print-error-toast__icon {
            background-color: #e9ecef;
        }
    }
}

@media screen and (max-width: 600px) {
    .print-error-toast__content {
        max-width: 100%;
    }
}
</style>

<style lang="scss">
.dark-layout {
    .print-error-toast {
        &--error {
            .print-error-toast__icon {
                background-color: #f8d7da !important;
            }
        }

        &--warning {
            .print-error-toast__icon {
                background-color: #fff3cd;
            }
        }

        &--info {
            .print-error-toast__icon {
                background-color: #e9ecef !important;
            }
        }
    }
}
</style>
